import { z } from "zod";
import i18n from "@/i18n";

// Schema for serial number validation
export const serialNumberSchema = z.object({
    serial_number: z.string().min(1, i18n.t("general.validation.global.required")),
});

// Schema for product information
export const productSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string()
});

// Schema for line items
export const lineSchema = z.object({
    id: z.number(),
    comment: z.string().nullish(),
    product: productSchema,
    picked_quantity: z.number().min(0),
    serial_numbers: z.array(serialNumberSchema)
});

// Main form schema
export const formScheme = z.object({
    lines: z.array(lineSchema)
})
    .superRefine((scheme, ctx) => {
        // 1. Create a map to store serial numbers and their paths
        // Key: serial_number (string)
        // Value: Array of paths, where each path indicates the location of that serial number
        // Example path: [lineIndex, "serial_numbers", serialIndex]
        const serialNumberPaths: Record<string, (string | number)[][]> = {};

        // 2. Iterate through lines and serial numbers to populate the map
        scheme.lines.forEach((line, lineIndex) => {
            line.serial_numbers.forEach((serial, serialIndex) => {
                const serialNumber = serial.serial_number;
                // Define the path to this specific serial number input field
                const path = [lineIndex, "serial_numbers", serialIndex];

                // Initialize the array for this serial number if it's the first time seeing it
                if (!serialNumberPaths[serialNumber]) {
                    serialNumberPaths[serialNumber] = [];
                }
                // Add the path to the list for this serial number
                serialNumberPaths[serialNumber].push(path);
            });
        });

        // 3. Iterate through the collected serial numbers to find duplicates
        Object.entries(serialNumberPaths).forEach(([serialNumber, paths]) => {
            // If a serial number appears in more than one path, it's a duplicate
            if (paths.length > 1) {
                // 4. Add a Zod issue for each occurrence of the duplicate serial number
                paths.forEach(path => {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom, // Use custom issue code
                        params: {
                            i18n: {
                                key: "sales.picking.modals.serialNumbers.validation.serialNumberDuplicate",
                            }
                        },
                        // Specify the exact path to the input field with the duplicate
                        path: ["lines", ...path, "serial_number"]
                    });
                });
            }
        });
    });

// Type for the form schema
export type SerialNumbersScheme = z.infer<typeof formScheme>;